<template>
  <div class="upload-links-wrap">
    <MenuBar />
    <div class="upload-links-page">
    <div class="page-header">
      <h2 class="page-title">📎 Upload Links History</h2>
      <p class="page-subtitle">YouTube &amp; Facebook upload records across all campaigns and direct uploads.</p>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <div class="filter-group">
        <label>Title</label>
        <input
          v-model="filters.title"
          type="text"
          placeholder="Search by title..."
          class="filter-input"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="filter-group">
        <label>Link (YouTube / Facebook)</label>
        <input
          v-model="filters.link"
          type="text"
          placeholder="Search by URL..."
          class="filter-input"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="filter-group">
        <label>Uploader Name</label>
        <div class="uploader-dropdown" ref="uploaderDropdown">
          <div
            class="uploader-input-wrap"
            :class="{ open: uploaderOpen }"
            @click="uploaderOpen = !uploaderOpen"
          >
            <span class="uploader-value">{{ filters.uploaderName || 'All uploaders' }}</span>
            <span class="uploader-arrow">{{ uploaderOpen ? '▲' : '▼' }}</span>
          </div>
          <div v-if="uploaderOpen" class="uploader-panel">
            <input
              ref="uploaderSearch"
              v-model="uploaderSearch"
              type="text"
              class="uploader-search"
              placeholder="Search uploaders..."
              @click.stop
            />
            <ul class="uploader-list">
              <li
                class="uploader-option"
                :class="{ selected: filters.uploaderName === '' }"
                @click="selectUploader('')"
              >All uploaders</li>
              <li
                v-for="name in filteredUploaders"
                :key="name"
                class="uploader-option"
                :class="{ selected: filters.uploaderName === name }"
                @click="selectUploader(name)"
              >{{ name }}</li>
              <li v-if="filteredUploaders.length === 0" class="uploader-empty">No match found</li>
            </ul>
          </div>
        </div>
      </div>
      <div class="filter-group">
        <label>Date From</label>
        <input
          v-model="filters.dateFrom"
          type="date"
          class="filter-input"
        />
      </div>
      <div class="filter-group">
        <label>Date To</label>
        <input
          v-model="filters.dateTo"
          type="date"
          class="filter-input"
        />
      </div>
      <div class="filter-group">
        <label>Upload Type</label>
        <select v-model="filters.uploadType" class="filter-select">
          <option value="all">All</option>
          <option value="direct">Direct</option>
          <option value="sponsor">Sponsor</option>
        </select>
      </div>
      <div class="filter-actions">
        <button class="btn-apply" @click="applyFilters" :disabled="loading">Apply</button>
        <button class="btn-reset" @click="resetFilters" :disabled="loading">Reset</button>
      </div>
    </div>

    <!-- Summary row -->
    <div class="summary-row" v-if="!loading">
      <span class="summary-text">
        {{ total }} record{{ total !== 1 ? 's' : '' }} found
        <span v-if="total > 0"> — Page {{ page }} of {{ totalPages }}</span>
      </span>
    </div>

    <!-- Loading -->
    <div class="loading-wrap" v-if="loading">
      <div class="spinner"></div>
      <span>Loading...</span>
    </div>

    <!-- Error -->
    <div class="error-banner" v-else-if="error">{{ error }}</div>

    <!-- Empty state -->
    <div class="empty-state" v-else-if="items.length === 0">
      <span>📭</span>
      <p>No upload links found for the selected filters.</p>
    </div>

    <!-- Table -->
    <div class="table-wrap" v-else>
      <table class="uploads-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>YouTube</th>
            <th>Facebook</th>
            <th>Uploaded Date</th>
            <th>Uploaded By</th>
            <th>Type</th>
            <th>Campaign</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in items" :key="item.id || idx">
            <td class="col-num">{{ (page - 1) * perPage + idx + 1 }}</td>
            <td class="col-title">{{ item.title || '—' }}</td>
            <td class="col-link">
              <a
                v-if="item.youtubeLink"
                :href="item.youtubeLink"
                target="_blank"
                rel="noopener noreferrer"
                class="link-yt"
              >▶ YouTube</a>
              <span v-else class="no-link">—</span>
            </td>
            <td class="col-link">
              <a
                v-if="item.facebookLink"
                :href="item.facebookLink"
                target="_blank"
                rel="noopener noreferrer"
                class="link-fb"
              >👍 Facebook</a>
              <span v-else class="no-link">—</span>
            </td>
            <td class="col-date">{{ item.uploadedDate || '—' }}</td>
            <td class="col-uploader">{{ item.createdBy || '—' }}</td>
            <td class="col-type">
              <span :class="['badge', item.uploadType === 'direct' ? 'badge-direct' : 'badge-sponsor']">
                {{ item.uploadType === 'direct' ? 'Direct' : 'Sponsor' }}
              </span>
            </td>
            <td class="col-campaign">{{ item.campaignName || '—' }}</td>
            <td class="col-createdat">{{ formatDate(item.createdAt) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="!loading && totalPages > 1">
      <button
        class="page-btn"
        :disabled="page <= 1"
        @click="goToPage(page - 1)"
      >← Prev</button>

      <template v-for="p in visiblePages">
        <span v-if="p === '...'" :key="'ellipsis-' + p" class="ellipsis">…</span>
        <button
          v-else
          :key="p"
          class="page-btn"
          :class="{ active: p === page }"
          @click="goToPage(p)"
        >{{ p }}</button>
      </template>

      <button
        class="page-btn"
        :disabled="page >= totalPages"
        @click="goToPage(page + 1)"
      >Next →</button>
    </div>
    </div>
  </div>
</template>

<script>
import api from '../api';
import MenuBar from './MenuBar.vue';

export default {
  name: 'UploadLinksHistory',
  components: { MenuBar },
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      perPage: 20,
      totalPages: 1,
      loading: false,
      error: '',
      uploaders: [],
      uploaderOpen: false,
      uploaderSearch: '',
      filters: {
        uploaderName: '',
        title: '',
        link: '',
        dateFrom: new Date().toISOString().slice(0, 10),
        dateTo: new Date().toISOString().slice(0, 10),
        uploadType: 'all',
      },
      // Applied filters (used for actual API calls)
      applied: {
        uploaderName: '',
        title: '',
        link: '',
        dateFrom: new Date().toISOString().slice(0, 10),
        dateTo: new Date().toISOString().slice(0, 10),
        uploadType: 'all',
      },
    };
  },
  computed: {
    filteredUploaders() {
      const q = this.uploaderSearch.toLowerCase();
      if (!q) return this.uploaders;
      return this.uploaders.filter(n => n.toLowerCase().includes(q));
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.page;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        if (current > 3) pages.push('...');
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (current < total - 2) pages.push('...');
        pages.push(total);
      }
      return pages;
    },
  },
  mounted() {
    this.fetchUploaders();
    this.fetchData();
    document.addEventListener('click', this.closeUploaderDropdown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeUploaderDropdown);
  },
  methods: {
    async fetchUploaders() {
      try {
        const res = await api.get('/upload-links/uploaders');
        this.uploaders = res.data.uploaders || [];
      } catch (_) {
        this.uploaders = [];
      }
    },
    selectUploader(name) {
      this.filters.uploaderName = name;
      this.uploaderOpen = false;
      this.uploaderSearch = '';
    },
    closeUploaderDropdown(e) {
      if (this.$refs.uploaderDropdown && !this.$refs.uploaderDropdown.contains(e.target)) {
        this.uploaderOpen = false;
        this.uploaderSearch = '';
      }
    },
    applyFilters() {
      this.applied = { ...this.filters };
      this.page = 1;
      this.fetchData();
    },
    resetFilters() {
      this.filters = { uploaderName: '', title: '', link: '', dateFrom: new Date().toISOString().slice(0, 10), dateTo: new Date().toISOString().slice(0, 10), uploadType: 'all' };
      this.applied = { ...this.filters };
      this.uploaderSearch = '';
      this.page = 1;
      this.fetchData();
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      this.error = '';
      try {
        const params = {
          page: this.page,
          perPage: this.perPage,
          uploadType: this.applied.uploadType,
        };
        if (this.applied.uploaderName) params.uploaderName = this.applied.uploaderName;
        if (this.applied.title) params.title = this.applied.title;
        if (this.applied.link) params.link = this.applied.link;
        if (this.applied.dateFrom) params.dateFrom = this.applied.dateFrom;
        if (this.applied.dateTo) params.dateTo = this.applied.dateTo;

        const res = await api.get('/upload-links', { params });
        this.items = res.data.items || [];
        this.total = res.data.total || 0;
        this.page = res.data.page || 1;
        this.perPage = res.data.perPage || 20;
        this.totalPages = res.data.totalPages || 1;
      } catch (err) {
        this.error =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Failed to load upload links.';
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleString('en-BD', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.upload-links-wrap {
  display: flex;
  min-height: 100vh;
  background: #f4f6f9;
}

.upload-links-page {
  flex: 1;
  margin-left: 250px;
  padding: 32px 28px;
  overflow-y: auto;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
}

.page-subtitle {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

/* Filter bar */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  background: #fff;
  border: 1px solid #e0e6ed;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
}

.filter-group label {
  font-size: 12px;
  font-weight: 600;
  color: #5a6a7a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-input,
.filter-select {
  padding: 9px 12px;
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  font-size: 14px;
  color: #2c3e50;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.2s;
}

.filter-input:focus,
.filter-select:focus {
  border-color: #3498db;
  background: #fff;
}

/* Searchable uploader dropdown */
.uploader-dropdown {
  position: relative;
  min-width: 180px;
}

.uploader-input-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  font-size: 14px;
  color: #2c3e50;
  background: #f8fafc;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.2s;
}

.uploader-input-wrap.open,
.uploader-input-wrap:hover {
  border-color: #3498db;
  background: #fff;
}

.uploader-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uploader-arrow {
  font-size: 10px;
  color: #95a5a6;
  margin-left: 8px;
}

.uploader-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  width: 220px;
  background: #fff;
  border: 1px solid #d0d8e4;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  z-index: 200;
  overflow: hidden;
}

.uploader-search {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: none;
  border-bottom: 1px solid #e8edf2;
  font-size: 13px;
  outline: none;
  color: #2c3e50;
  background: #f8fafc;
}

.uploader-search:focus {
  background: #fff;
}

.uploader-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 220px;
  overflow-y: auto;
}

.uploader-option {
  padding: 9px 14px;
  font-size: 13px;
  color: #2c3e50;
  cursor: pointer;
  transition: background 0.15s;
}

.uploader-option:hover {
  background: #eaf2fb;
}

.uploader-option.selected {
  background: #dbeeff;
  color: #2980b9;
  font-weight: 600;
}

.uploader-empty {
  padding: 10px 14px;
  font-size: 13px;
  color: #95a5a6;
  font-style: italic;
}

.filter-actions {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.btn-apply {
  padding: 9px 22px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-apply:hover:not(:disabled) {
  opacity: 0.88;
}

.btn-apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-reset {
  padding: 9px 16px;
  background: #ecf0f1;
  color: #5a6a7a;
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-reset:hover:not(:disabled) {
  background: #dde4ea;
}

/* Summary */
.summary-row {
  margin-bottom: 12px;
}

.summary-text {
  font-size: 13px;
  color: #7f8c8d;
  font-weight: 500;
}

/* Loading */
.loading-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 40px;
  justify-content: center;
  color: #7f8c8d;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e0e6ed;
  border-top-color: #3498db;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error */
.error-banner {
  background: #fdf0f0;
  border: 1px solid #f5c6cb;
  color: #c0392b;
  border-radius: 8px;
  padding: 14px 18px;
  font-size: 14px;
  margin-bottom: 16px;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #95a5a6;
}

.empty-state span {
  font-size: 40px;
}

.empty-state p {
  margin-top: 12px;
  font-size: 15px;
}

/* Table */
.table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.uploads-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 14px;
}

.uploads-table th {
  background: #2c3e50;
  color: #ecf0f1;
  padding: 13px 14px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
}

.uploads-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #edf0f4;
  vertical-align: middle;
  color: #2c3e50;
}

.uploads-table tbody tr:hover {
  background: #f7f9fc;
}

.uploads-table tbody tr:last-child td {
  border-bottom: none;
}

.col-num {
  width: 46px;
  color: #95a5a6;
  font-size: 12px;
  text-align: center;
}

.col-title {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-link {
  white-space: nowrap;
}

.link-yt {
  color: #e74c3c;
  font-weight: 600;
  text-decoration: none;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  background: #fdf0f0;
  transition: background 0.15s;
}

.link-yt:hover {
  background: #fbd7d7;
}

.link-fb {
  color: #2c3e8b;
  font-weight: 600;
  text-decoration: none;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  background: #eaf0ff;
  transition: background 0.15s;
}

.link-fb:hover {
  background: #d0deff;
}

.no-link {
  color: #bdc3c7;
  font-size: 13px;
}

.col-date,
.col-uploader,
.col-createdat {
  white-space: nowrap;
  font-size: 13px;
}

.col-campaign {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #7f8c8d;
}

.badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

.badge-direct {
  background: #e8f8f5;
  color: #1a8a6f;
  border: 1px solid #a8e6d8;
}

.badge-sponsor {
  background: #fef9e7;
  color: #b7860b;
  border: 1px solid #f5d97e;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 0 8px;
  flex-wrap: wrap;
}

.page-btn {
  padding: 8px 14px;
  border: 1px solid #d0d8e4;
  background: #fff;
  color: #2c3e50;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled):not(.active) {
  background: #eaf2fb;
  border-color: #3498db;
  color: #3498db;
}

.page-btn.active {
  background: #3498db;
  border-color: #3498db;
  color: white;
  cursor: default;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ellipsis {
  padding: 8px 4px;
  color: #95a5a6;
  font-size: 14px;
}
</style>
